// apps/merchant-v3/src/components/guards/SettingsRoleGuard.tsx
//
// Phase 6.1 Wave 5 Task 24 — FE role-gate for the Members / Teams settings
// surfaces (P6.1.D-27, G-15, TS-15). The API already returns 403 for
// staff/viewer (Wave 3 + Task 18); this guard mirrors that at the route
// layer so a staff member typing `/settings/team` (or /settings/member,
// /settings/teams) directly lands on the 403 page instead of a blank/crash.
//
// Access tiers (from usePermissions.settingsAccess):
//   - 'full'     → owner/admin: render children unrestricted.
//   - 'readonly' → manager: render children inside a read-only context
//                  (SettingsReadOnlyProvider) so mutating controls hide.
//   - 'none'     → staff/viewer: <Navigate to="/403" replace />.
//   - 'loading'  → defer render (LoadingShell) until perms resolve — never
//                  flash the 403 page for an owner mid-bootstrap.
//
// vg-binding: PLAN:task-24
// vg-binding: P6.1.D-27
// vg-binding: P6.1.D-02
// vg-binding: G-15
// vg-binding: G-36
// vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:error-shape

import { createContext, useContext, type JSX, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { LoadingShell } from '@/components/LoadingShell';
import { usePermissions } from '@/lib/scope/usePermissions';

/** Read-only mode flag exposed to gated settings pages. `true` for manager. */
const SettingsReadOnlyContext = createContext<boolean>(false);

/**
 * Read whether the current settings surface is rendered read-only (manager).
 * Pages call this to hide/disable create/edit/toggle controls (D-02/D-27).
 * Defaults to `false` (full access) outside the guard.
 */
export function useSettingsReadOnly(): boolean {
  return useContext(SettingsReadOnlyContext);
}

interface SettingsRoleGuardProps {
  children: ReactNode;
}

export function SettingsRoleGuard({
  children,
}: SettingsRoleGuardProps): JSX.Element {
  const { settingsAccess } = usePermissions();

  if (settingsAccess === 'loading') {
    return <LoadingShell />;
  }

  if (settingsAccess === 'none') {
    // staff/viewer — mirror the API 403 at the route layer (TS-15).
    return <Navigate to="/403" replace />;
  }

  const readOnly = settingsAccess === 'readonly';
  return (
    <SettingsReadOnlyContext.Provider value={readOnly}>
      {children}
    </SettingsReadOnlyContext.Provider>
  );
}

export default SettingsRoleGuard;
